import { useEffect, useState } from 'react'

/**
 * LE TIC — une seule horloge pour toute la téléphonie v2.
 *
 * Contrainte de perf du cahier : « aucun `setInterval` hors le tic unique de
 * la machine ». Avant ce module il y en avait deux, chacun dans son coin : le
 * rebours de l'auto-dial (`useCampaignDialer`) et le chronomètre d'appel
 * (`ActiveCallPanel`) — deux minuteries non synchronisées, deux sources de
 * rendus par seconde, et rien qui garantisse qu'un onglet en arrière-plan ne
 * les laisse dériver l'une de l'autre.
 *
 * Ici : UNE minuterie, démarrée au premier abonné et arrêtée au dernier. Elle
 * ne porte aucune logique — elle dit seulement « une seconde a passé ». Qui
 * fait quoi de cette seconde reste chez l'abonné (la machine décide de
 * l'échéance du rebours, le chronomètre se redessine).
 *
 * Un abonné qui lève ne peut pas empêcher les autres d'être notifiés (même
 * précaution que les diffusions de `callMachine`).
 */

type TickListener = () => void

const listeners = new Set<TickListener>()
let timer: ReturnType<typeof setInterval> | null = null

export const TICK_INTERVAL_MS = 1000

function tick(): void {
  Array.from(listeners).forEach((listener) => {
    try {
      listener()
    } catch (error) {
      console.error('[telephony-v2] Abonné du tic en erreur', error)
    }
  })
}

/** @returns le désabonnement. La minuterie s'arrête avec le dernier abonné. */
export function subscribeTick(listener: TickListener): () => void {
  listeners.add(listener)
  if (!timer) timer = setInterval(tick, TICK_INTERVAL_MS)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && timer) {
      clearInterval(timer)
      timer = null
    }
  }
}

/** Abonnés en cours — diagnostic et tests (jamais de logique dessus). */
export function tickListenerCount(): number {
  return listeners.size
}

/**
 * Redemande un rendu à CHAQUE seconde, tant que `active`.
 *
 * À n'utiliser que dans un composant qui n'affiche QUE du temps : c'est lui,
 * et lui seul, qui se redessine. Un compteur branché sur la scène entière
 * ferait re-rendre la fiche contact, le script et l'historique une fois par
 * seconde — le défaut de perf que ce lot est chargé de ne pas introduire.
 */
export function useTick(active: boolean): void {
  const [, setCount] = useState(0)
  useEffect(() => {
    if (!active) return
    return subscribeTick(() => setCount((value) => value + 1))
  }, [active])
}
